import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { track, identify, reset as analyticsReset } from "@/lib/analytics";
import { clearStoredWorksheet } from "@/context/AnalysisContext";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If OAuth callback in-flight, skip
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.user_id) identify(data.user_id);
    } catch { setUser(false); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    if (data?.user_id) identify(data.user_id);
    return data;
  };
  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    if (data?.user_id) identify(data.user_id);
    track("account_created");
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { console.warn("Logout API failed", e); }
    setUser(false);
    analyticsReset();
    // Wipe worksheet autosave — contains sensitive financial fields; on a
    // shared device the next user should not see the previous user's data.
    clearStoredWorksheet();
  };
  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
